"use client";

import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

interface CaseStudyReadCardProps {
  title: string;
  href: string;
  imageSrc: string;
  imageAlt: string;
  ctaText?: string;
  className?: string;
}

export function CaseStudyReadCard({
  title,
  href,
  imageSrc,
  imageAlt,
  ctaText = "Read more",
  className,
}: CaseStudyReadCardProps) {
  return (
    <section className={cn("mt-10 lg:max-w-4xl", className)} aria-label="Read related case study">
      <Link
        href={href}
        className="group block rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-gray-300 md:p-6"
      >
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
          <div className="relative h-36 w-full shrink-0 overflow-hidden rounded-xl sm:h-40 sm:w-44">
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              sizes="(max-width: 640px) 100vw, 176px"
              className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ss-blue">Read the case study</p>
            <h3 className="mt-2 text-xl font-semibold leading-tight text-gray-900 md:text-2xl">{title}</h3>
            <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-ss-blue px-4 py-2 text-sm font-semibold text-ss-blue transition-colors group-hover:bg-ss-blue group-hover:text-white">
              {ctaText}
              <ArrowUpRight className="size-4" />
            </span>
          </div>
        </div>
      </Link>
    </section>
  );
}
